import { FormatType } from '@revelio/iso-utility';

import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import { FilterId } from '../../filters/types/filters';
import {
  demandSupplyRatioRankInfoText,
  marketTightnessInfoText,
  timeToFillRankInfoText,
  wagePressureRankInfoText,
} from '../market-conditions-info-text';

const items: TabContent['items'] = [
  {
    id: 'a5897419-0a41-4c45-b279-97868a9a4098',
    title: 'Demand/Supply Ratio',
    isTitleManuallySet: true,
    position: { x: 0, y: 12, width: 10, height: 11 },
    info: { text: demandSupplyRatioRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "demand_supply_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'c31dd804-73c3-4a7d-8792-5f794477aa33',
    title: 'Market Tightness',
    isTitleManuallySet: true,
    position: { x: 0, y: 0, width: 30, height: 12 },
    info: { text: marketTightnessInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "market_tightness"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'eb7681fe-cb31-488f-851b-231bf8c45b6c',
    title: 'Demand/Supply Ratio',
    isTitleManuallySet: true,
    position: { x: 0, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['demand_supply'],
      format: FormatType.RATIO,
    },
  },
  {
    id: '6e6d48a2-d611-488b-a3f4-bb736779a4f1',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['wage_pressure'],
      format: FormatType.PERCENTAGE,
    },
  },
  {
    id: '8162b197-b290-45a2-853a-52819a955cae',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 12, width: 10, height: 11 },
    info: { text: wagePressureRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      defaultMetricOptions: ['wage_pressure_percentile'],
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "wage_pressure_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'f2049a60-e78e-4a09-8cb5-c40162a08cbd',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['time_to_fill'],
      format: FormatType.DAY,
    },
  },
  {
    id: '5cbfe3d2-b694-4e6d-929f-53e3c2362acd',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 12, width: 10, height: 11 },
    info: { text: timeToFillRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "time_to_fill_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'c5e440b6-ae22-4dbc-a7da-09e8dafe43f1',
    title: 'Postings by Company',
    isTitleManuallySet: true,
    position: { x: 30, y: 0, width: 18, height: 36 },
    plot: {
      chartType: 'grouped-bar',
      secondaryDimension: 'company',
      defaultMetricOptions: ['postings_active'],
      format: FormatType.DECIMAL,
    },
  },
];

const tabContent: TabContent = {
  id: RevelioPageId.GEOGRAPHY_MARKET_CONDITIONS,
  primaryFilter: {
    id: FilterId.GEOGRAPHY,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.ROLE,
    FilterId.CUSTOM_SKILL,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  items,
};

export const geographyMarketConditions: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.GEOGRAPHY_MARKET_CONDITIONS,
  revelioPageId: RevelioPageId.GEOGRAPHY_MARKET_CONDITIONS,
  name: 'Market Conditions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: RevelioPageId.GEOGRAPHY_MARKET_CONDITIONS,
      label: 'Market Conditions',
      isActive: true,
      tabContent,
    },
  ],
};
